import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { q } from '@actual-app/core/shared/query';

import { HojaSeleccion } from '#components/mobile/ui/HojaSeleccion';
import { useCategories } from '#hooks/useCategories';
import { usePayees } from '#hooks/usePayees';
import type { Modal as ModalType } from '#modals/modalsSlice';
import { useCreatePayeeMutation } from '#payees';
import { aqlQuery } from '#queries/aqlQuery';

import {
  guardarBeneficiarioReciente,
  habitualesPorBeneficiario,
  leerBeneficiariosRecientes,
  seccionesBeneficiario,
} from './seleccionBeneficiario';

type OpcionesModal = Extract<
  ModalType,
  { name: 'payee-autocomplete' }
>['options'];

type HojaBeneficiarioProps = OpcionesModal & {
  /** Quita la hoja de la pila de modales (y avisa a `onClose`). */
  onCerrar: () => void;
  /** «Traspaso entre cuentas»: abre la lista de cuentas de siempre. */
  onTraspaso?: () => void;
};

/** Movimientos que se miran para la categoría habitual de cada uno. */
const LIMITE_HISTORIAL = 3000;

/**
 * Selector de beneficiario móvil (H71): la hoja de selección de Rumbo con
 * «Recientes» y «Todos», la categoría habitual bajo cada nombre y «Crear»
 * desde el buscador.
 */
export function HojaBeneficiario({
  onSelect,
  onCerrar,
  onTraspaso,
}: HojaBeneficiarioProps) {
  const { t } = useTranslation();
  const { data: beneficiarios = [] } = usePayees();
  const { data: { list: categorias } = { list: [] } } = useCategories();
  const crearBeneficiario = useCreatePayeeMutation();
  const [recientes] = useState(leerBeneficiariosRecientes);
  const [habituales, setHabituales] = useState<Map<string, string>>(
    () => new Map(),
  );

  useEffect(() => {
    let vivo = true;
    aqlQuery(
      q('transactions')
        .filter({ payee: { $ne: null }, category: { $ne: null } })
        .orderBy({ date: 'desc' })
        .select(['payee', 'category'])
        .limit(LIMITE_HISTORIAL),
    )
      .then(({ data }) => {
        if (vivo) {
          setHabituales(
            habitualesPorBeneficiario(
              data as Array<{ payee: string | null; category: string | null }>,
            ),
          );
        }
      })
      .catch(() => {
        // Sin categoría habitual: la hoja sigue funcionando.
      });
    return () => {
      vivo = false;
    };
  }, []);

  const secciones = useMemo(() => {
    const nombreCategoria = new Map<string, string>();
    for (const c of categorias) {
      nombreCategoria.set(c.id, c.name);
    }
    return seccionesBeneficiario({
      beneficiarios,
      recientes,
      habitual: id => {
        const cat = habituales.get(id);
        return cat ? nombreCategoria.get(cat) : undefined;
      },
      titulos: { recientes: t('Recent'), todos: t('All payees') },
    });
  }, [beneficiarios, categorias, habituales, recientes, t]);

  const elegir = (id: string) => {
    guardarBeneficiarioReciente(id);
    onSelect(id);
    onCerrar();
  };

  return (
    <HojaSeleccion
      data-testid="hoja-beneficiario"
      titulo={t('Payee')}
      textoBuscar={t('Search or create payee…')}
      secciones={secciones}
      onElegir={fila => elegir(fila.id)}
      onCerrar={onCerrar}
      filaFinal={
        onTraspaso
          ? { nombre: t('Transfer between accounts'), onElegir: onTraspaso }
          : undefined
      }
      crear={{
        etiqueta: nombre => t('Create payee "{{name}}"', { name: nombre }),
        onCrear: nombre => {
          if (!nombre) {
            return;
          }
          void crearBeneficiario
            .mutateAsync({ name: nombre })
            .then(id => elegir(id))
            .catch(() => {
              // El aviso de error ya lo da la mutación.
            });
        },
      }}
    />
  );
}
